import { useEffect, useRef, useState } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { useFxRates } from "../utils/fxRates";
import { fetchMarketData } from "../utils/marketdata";
import { tlvTime, nyTime } from "../utils/marketHours";
import { dateNumeric } from "../utils/formatDate";
import { takeScroll } from "../utils/scrollTarget";
import { useIsMobile } from "../utils/hooks";
import IndexChartPanel from "./IndexChartPanel";
import { Amount } from "./Money";
import LTR from "./LTR";
import Icon from "./Icon";

// ─── "שווקים" (26/10/2026, mockup 3) ──────────────────────────────────────────
// The portfolio page's market card: it was "שוק ישראלי / שוק אמריקאי" with two
// flags, and is now three tabs · ישראל / ארה"ב / עולם · four series in each.
// The one place in the app that lists them: the home card's "כל המדדים ←" and
// the intelligence page's "תמונת השוק" link here instead of repeating them.
//
// A cell opens its chart (IndexChartPanel), except the Bank of Israel rate.
// Every cell: the value, the day's change, the moment it is from and its
// source. An intraday quote carries the time the exchange stamped it (Israel
// time); a FRED close or the Bank of Israel rate carries its date. Nothing is
// said about whether a market is open: outside the US there is no live source
// for that (DECISIONS.md, sprint 19), and a time from this morning says it.
// A series without an answer is "—" with no change, never a number.

export const MARKETS_SCROLL_ID = "markets";

// What the index measures, in one line under its chart, from the index
// provider's own methodology document (David, 02/11/2026 · it was a panel of
// its own, IndexDetailPanel, until 26/10).
const ABOUT = {
  sp500:  { text: "500 חברות גדולות בארה״ב, משוקלל לפי שווי השוק של המניות הזמינות למסחר בציבור",
            source: { text: "S&P Dow Jones Indices", url: "https://www.spglobal.com/spdji/en/documents/methodologies/methodology-sp-us-indices.pdf" } },
  nasdaq: { text: "כל המניות הרגילות, אמריקאיות וזרות, שרשומות ב-Nasdaq, משוקלל לפי שווי שוק",
            source: { text: "Nasdaq", url: "https://indexes.nasdaqomx.com/docs/Methodology_COMP.pdf" } },
  dow:    { text: "30 מניות, משוקלל לפי מחיר המניה ולא לפי גודל החברה",
            source: { text: "S&P Dow Jones Indices", url: "https://www.spglobal.com/spdji/en/documents/methodologies/methodology-dj-averages.pdf" } },
};

// The tabs and their series, in the order of the mockup.
export const MARKET_TABS = [
  { id: "il", label: "ישראל", clock: () => tlvTime(), clockTz: "שעון תל אביב",
    rows: [
      { key: "ta35",    label: "ת״א 35",   yahoo: "TA35.TA",   tv: "TASE:TA35",  delay: 20 },
      { key: "ta125",   label: "ת״א 125",  yahoo: "^TA125.TA", tv: "TASE:TA125", delay: 20 },
      { key: "usdIls",  label: "USD/ILS",  yahoo: "ILS=X",     tv: "FX_IDC:USDILS", delay: 0, latin: true, points: false },
      { key: "boiRate", label: "ריבית בנק ישראל" },
    ],
    foot: "ת״א 35 ות״א 125: Yahoo Finance, בעיכוב של 20 דקות · USD/ILS: כל 15 דקות · ריבית: בנק ישראל" },
  { id: "us", label: "ארה״ב", clock: () => nyTime(), clockTz: "שעון ניו יורק",
    rows: [
      { key: "sp500",   label: "S&P 500",      yahoo: "^GSPC", tv: "SP:SPX",      delay: 0,  latin: true, about: ABOUT.sp500 },
      { key: "nasdaq",  label: "Nasdaq",       yahoo: "^IXIC", tv: "NASDAQ:IXIC", delay: 0,  latin: true, about: ABOUT.nasdaq },
      { key: "dow",     label: "Dow Jones",    yahoo: "^DJI",  tv: "DJ:DJI",      delay: 0,  latin: true, about: ABOUT.dow },
      { key: "russell", label: "Russell 2000", yahoo: "^RUT",  tv: "TVC:RUT",     delay: 15, latin: true },
    ],
    foot: "Yahoo Finance · S&P 500, Nasdaq ו-Dow כמעט בזמן אמת · Russell 2000 בעיכוב של 15 דקות · כשאין ציטוט: סגירה יומית מ-FRED" },
  { id: "world", label: "עולם", clock: null,
    rows: [
      { key: "dax",    label: "DAX",        yahoo: "^GDAXI", tv: "XETR:DAX",  delay: 15, latin: true },
      { key: "ftse",   label: "FTSE 100",   yahoo: "^FTSE",  tv: "TVC:UKX",   delay: 15, latin: true },
      { key: "nikkei", label: "Nikkei 225", yahoo: "^N225",  tv: "TVC:NI225", delay: 30, latin: true },
      { key: "hsi",    label: "Hang Seng",  yahoo: "^HSI",   tv: "TVC:HSI",   delay: 15, latin: true },
    ],
    foot: "Yahoo Finance · DAX, FTSE 100 ו-Hang Seng בעיכוב של 15 דקות · Nikkei 225 בעיכוב של 30 דקות · השעות לפי שעון ישראל" },
];

const fmtIdx = n => (n >= 1000
  ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : n.toFixed(2));

// "26/10 · 17:24", Israel time · the moment the exchange stamped the quote.
export function quoteMoment(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Jerusalem", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(d).map(p => [p.type, p.value]));
  return `${parts.day}/${parts.month} · ${parts.hour}:${parts.minute}`;
}

// One cell as shown: { value, change, when, source } · or the empty cell.
export function cellOf(key, md, fx) {
  if (key === "usdIls") {
    const ils = fx?.ILS;
    if (!ils?.rate) return null;
    const yahoo = /Yahoo/.test(ils.source || "");
    return { amount: ils.rate, display: `₪${ils.rate.toFixed(3)}`, change: yahoo ? (ils.change_pct ?? null) : null,
      when: ils.at ? quoteMoment(ils.at) : ils.date ? dateNumeric(ils.date) : null,
      source: yahoo ? "Yahoo Finance" : "ExchangeRate-API" };
  }
  const q = md?.[key];
  if (!q || !Number.isFinite(q.value)) return null;
  if (key === "boiRate") return { value: `${q.value.toFixed(2)}%`, display: `${q.value.toFixed(2)}%`, change: null, rate: true, when: q.date ? dateNumeric(q.date) : null, source: "בנק ישראל" };
  // A quote with the exchange's stamp is Yahoo's; without one it is FRED's
  // dated close (only the S&P 500, Nasdaq and Dow have that fallback).
  return q.quote_ts
    ? { value: fmtIdx(q.value), display: fmtIdx(q.value), change: q.change_pct, when: quoteMoment(q.quote_ts), source: "Yahoo Finance" }
    : { value: fmtIdx(q.value), display: fmtIdx(q.value), change: q.change_pct, when: q.date ? `סגירה ${dateNumeric(q.date)}` : null, source: "FRED" };
}

function Change({ change, rate }) {
  if (rate) return <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 3 }}>שיעור נוכחי</div>;
  if (change == null || !Number.isFinite(change)) return <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 3 }}>&nbsp;</div>;
  // A change that rounds to 0.00% is no change: no arrow, no colour.
  const flat = Math.abs(change) < 0.005;
  return (
    <LTR className="si-num" style={{ display: "block", fontSize: 13, fontWeight: 700, marginTop: 3, color: flat ? UI.text.muted : change > 0 ? UI.gain : UI.loss }}>
      {flat ? "" : change > 0 ? "▲ " : "▼ "}{Math.abs(change).toFixed(2)}%
    </LTR>
  );
}

export default function MarketsCard() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("il");
  const [md, setMd] = useState(null);
  const [selected, setSelected] = useState(null);   // the row whose chart is open
  const fx = useFxRates();
  const ref = useRef(null);
  // The exchange clock moves on its own, not only when the figures refresh.
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick(n => n + 1), 30 * 1000); return () => clearInterval(id); }, []);

  useEffect(() => {
    let alive = true;
    const load = () => fetchMarketData().then(d => { if (alive) setMd(d || {}); });
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Arriving from "כל המדדים ←" (home) or "תמונת השוק" (intelligence): to
  // this card, on the tab the link named. Same handshake as the watchlist
  // (utils/scrollTarget.js): the request waits if this screen mounts later.
  useEffect(() => {
    const go = id => {
      const want = MARKET_TABS.find(t => id === `${MARKETS_SCROLL_ID}:${t.id}`);
      if (!want || !takeScroll(id)) return;
      setTab(want.id);
      setSelected(null);
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = setTimeout(() => MARKET_TABS.forEach(x => go(`${MARKETS_SCROLL_ID}:${x.id}`)), 450);
    const onEvent = e => { if (typeof e.detail?.id === "string") go(e.detail.id); };
    window.addEventListener("si-scroll-to", onEvent);
    return () => { clearTimeout(t); window.removeEventListener("si-scroll-to", onEvent); };
  }, []);

  const cur = MARKET_TABS.find(t => t.id === tab) || MARKET_TABS[0];
  const clock = cur.clock ? cur.clock() : null;
  const loading = md === null;

  return (
    <div ref={ref} id="markets-card" data-markets-card style={{ scrollMarginTop: 130, background: UI.surface.raised, borderRadius: T.radius.xl, padding: isMobile ? "14px 14px" : "16px 20px", border: `1px solid ${UI.line.hairline}`, boxShadow: T.shadowCard }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ ...TYPE.heading, fontSize: 18, margin: 0, color: UI.text.primary }}>שווקים</h2>
          <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 1 }}>מדדים, שער הדולר וריבית בנק ישראל</div>
        </div>
        <div role="tablist" aria-label="שוק" style={{ display: "flex", gap: 2, background: UI.fill.subtle, borderRadius: 8, padding: 2, flexShrink: 0 }}>
          {MARKET_TABS.map(t => {
            const on = t.id === tab;
            return (
              <button key={t.id} role="tab" aria-selected={on} onClick={() => { setTab(t.id); setSelected(null); }}
                style={{ minHeight: 36, minWidth: isMobile ? 0 : 64, padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                  background: on ? UI.state.surface : "transparent", color: on ? UI.state.fg : UI.text.secondary }}>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* The exchange's clock · a time, not a claim that it is trading. */}
      {clock && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
          <Icon name="clock" size={13} color={UI.text.muted}/>
          <LTR className="si-num" style={{ fontSize: 13, fontWeight: 700, color: UI.text.secondary }}>{clock}</LTR>
          <span style={{ fontSize: 12, color: UI.text.muted }}>· {cur.clockTz}</span>
        </div>
      )}

      <div role="tabpanel" style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: isMobile ? 6 : 8, marginTop: 12 }}>
        {cur.rows.map(row => {
          const c = loading ? undefined : cellOf(row.key, md, fx);
          // A series with a chart opens it (IndexChartPanel) · the Bank of
          // Israel rate has none.
          const clickable = !!(c && row.yahoo);
          const active = clickable && selected?.key === row.key;
          return (
            <div key={row.key} data-market-cell={row.key}
              role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `${row.label} · גרף` : undefined}
              onClick={clickable ? () => setSelected(row) : undefined}
              onKeyDown={clickable ? e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(row); } } : undefined}
              style={{ background: UI.surface.sunken, borderRadius: 6, padding: 10, minWidth: 0, cursor: clickable ? "pointer" : "default",
                border: `1px solid ${active ? UI.text.secondary : c ? UI.state.line : UI.line.hairline}` }}>
              <div style={{ ...TYPE.headingSub, fontSize: 12, color: UI.text.secondary, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.latin ? <LTR style={{ fontFamily: FONT.latin }}>{row.label}</LTR> : row.label}
                {clickable && <span style={{ marginInlineStart: "auto", fontSize: 11, color: UI.text.muted, opacity: .7 }}>↗</span>}
              </div>
              <div className="si-num" style={{ ...TYPE.value, fontWeight: 800, fontVariantNumeric: "tabular-nums", marginTop: 5, direction: "ltr", textAlign: "right" }}>
                {c === undefined ? "…" : !c ? "—" : c.amount != null ? <Amount value={c.amount} currency="ILS" decimals={3} size={null} weight={null}/> : c.value}
              </div>
              {c ? <Change change={c.change} rate={c.rate}/> : <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 3 }}>{c === null ? "אין נתון" : " "}</div>}
              {c && (c.when || c.source) && (
                <div style={{ fontSize: 11, color: UI.text.muted, marginTop: 6, lineHeight: 1.45 }}>
                  {c.when && <LTR className="si-num">{c.when}</LTR>}{c.when && c.source ? " · " : ""}{c.source && <LTR>{c.source}</LTR>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selected && (
        <IndexChartPanel series={selected} cell={cellOf(selected.key, md, fx)} onClose={() => setSelected(null)}/>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11.5, color: UI.text.muted, marginTop: 10, lineHeight: 1.55 }}>
        <Icon name="check" size={13} color={UI.text.secondary} style={{ marginTop: 2 }}/>
        <span>{cur.foot}</span>
      </div>
    </div>
  );
}
